import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, Users, Webhook, BellRing, KeyRound, FileSpreadsheet,
  Settings, LogOut, BookOpen, ExternalLink, Menu, X,
} from "lucide-react";
import { atlasFetch, getStoredKey, keyMode, signOut, ATLAS_BASE } from "@/lib/atlas-api";

const NAV_ITEMS = [
  { to: "/portal", key: "dashboard", icon: LayoutDashboard, end: true, testId: "portal-nav-dashboard" },
  { to: "/portal/users", key: "users", icon: Users, testId: "portal-nav-users" },
  { to: "/portal/webhooks", key: "webhooks", icon: Webhook, testId: "portal-nav-webhooks" },
  { to: "/portal/usage-alerts", key: "usageAlerts", icon: BellRing, testId: "portal-nav-alerts" },
  { to: "/portal/reports", key: "reports", icon: FileSpreadsheet, testId: "portal-nav-reports" },
  { to: "/portal/keys", key: "keys", icon: KeyRound, testId: "portal-nav-keys" },
  { to: "/portal/settings", key: "settings", icon: Settings, testId: "portal-nav-settings" },
];

export default function PortalLayout() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("portal");
  const NAV = NAV_ITEMS.map((n) => ({ ...n, label: t(`nav.${n.key}`) }));
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const mode = keyMode();

  // Portal routes are outside the LangGate — apply the user's stored
  // language once on mount so the sidebar labels + UI copy match what
  // they picked on the marketing site.
  useEffect(() => {
    try {
      const stored = localStorage.getItem("atlasforge_lang");
      if (stored && stored !== i18n.language) {
        i18n.changeLanguage(stored);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!getStoredKey()) {
      navigate("/portal/login", { replace: true });
      return;
    }
    atlasFetch("/api/v1/me").then(setMe).catch(() => { /* 401 redirects */ });
  }, [navigate]);

  const refreshMe = (patch) => {
    if (patch) {
      setMe((prev) => (prev ? { ...prev, ...patch } : prev));
      return;
    }
    atlasFetch("/api/v1/me").then(setMe).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-[#F5F1EB]/40 flex">
      <aside
        data-testid="portal-sidebar"
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#1A4232] text-[#F5F1EB] flex flex-col transition-transform duration-200 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-6 py-6 flex items-center justify-between">
          <Link to="/portal" className="font-display text-[18px] tracking-tight">
            AtlasForge<span className="opacity-60"> · Portal</span>
          </Link>
          <button
            onClick={() => setOpen(false)}
            data-testid="portal-sidebar-close"
            className="lg:hidden text-[#F5F1EB]/70 hover:text-[#F5F1EB]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-2 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testId}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-[10px] px-3.5 py-2.5 text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? "bg-[#F5F1EB] text-[#1A4232]"
                      : "text-[#F5F1EB]/80 hover:bg-[#F5F1EB]/10 hover:text-[#F5F1EB]"
                  }`
                }
              >
                <Icon className="h-4 w-4" strokeWidth={2} />
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-[#F5F1EB]/10 space-y-1">
          <a
            href={`${ATLAS_BASE}/api/v1/integration-guide`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="portal-nav-docs"
            className="flex items-center gap-3 rounded-[10px] px-3.5 py-2.5 text-[13.5px] text-[#F5F1EB]/70 hover:bg-[#F5F1EB]/10 hover:text-[#F5F1EB]"
          >
            <BookOpen className="h-4 w-4" />
            Integration Guide
            <ExternalLink className="h-3 w-3 ml-auto opacity-60" />
          </a>
          <button
            onClick={signOut}
            data-testid="portal-sign-out"
            className="w-full flex items-center gap-3 rounded-[10px] px-3.5 py-2.5 text-[13.5px] text-[#F5F1EB]/70 hover:bg-[#F5F1EB]/10 hover:text-[#F5F1EB]"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          aria-hidden
        />
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header
          data-testid="portal-topbar"
          className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200/70 px-4 sm:px-6 lg:px-10 h-14 flex items-center gap-3"
        >
          <button
            onClick={() => setOpen(true)}
            data-testid="portal-sidebar-open"
            className="lg:hidden -ml-1 p-2 rounded-md hover:bg-slate-100 text-[#1A4232]"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="font-display text-[15px] text-[#0A1128] truncate">
            {me?.company || me?.name || "…"}
          </div>
          {mode && (
            <span
              data-testid="portal-key-mode-pill"
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                mode === "production"
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-amber-50 text-amber-800 border border-amber-200"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${mode === "production" ? "bg-emerald-500" : "bg-amber-500"}`} />
              {mode === "production" ? "PRODUCTION" : "SANDBOX"}
            </span>
          )}
          <div className="ml-auto hidden sm:flex items-center gap-3 text-[12px] text-slate-500">
            <span data-testid="portal-tier">{me?.tier || ""}</span>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-8 max-w-6xl w-full mx-auto">
          <Outlet context={{ me, refreshMe }} />
        </main>
      </div>
    </div>
  );
}
